import { FC, useContext, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'next-i18next';
import classNames from 'classnames';
import { ReactSVG } from 'react-svg';
import {
  ButtonsContainer,
  LoadingButton,
  Tabs,
  FillingItemSkelet,
  calculateTotalValues,
  mapSidesToFilling,
  mapTotalForCalc,
  ITemplateSide,
  ThemeContext,
  Button,
  ITemplateRenderData,
  ITemplateBlocksTree,
} from '@forma/forma-ui-kit';
import FillDocumentSide from '@/components/views/FillDocument/FillDocumentSide';
import { checkSideValid } from '@/components/views/FillDocument/utils';
import Link from '@/components/Link';

import { ISidesListEditData } from '@/helpers/interfaces/templates.interface';
import { analytics } from '@/helpers/analytics';

import styles from './fill-document.module.css';

interface ISidesSendingData {
  [sideId: string]: {
    tattrs: string[],
    tables: string[],
    articles: string[],
    tattrFolders: string[]
  }
}

interface FillDocumentProps {
  sides?: ITemplateSide[],
  blocksTree?: ITemplateBlocksTree,
  onClickDownload: (sideValues: ITemplateRenderData['sideValues'], files: (File | null)[]) => void,
  isDownloadLoading?: boolean,
  valueSides?: ISidesListEditData,
  urlKey?: string
  showFillMessage?: boolean
}

const cleanSideDataForRender = (sidesData: ISidesListEditData) => (
  Object.values(sidesData).map(({ tableValues, tattrErrors, tattrValues, isValid, ...rest }) => ({
    tattrValues: Object.keys(tattrValues).reduce((acc: { [key: string]: string }, key) => (
      tattrValues[key] ? { ...acc, [key]: tattrValues[key].replace(/<br \/>$/, '') } : acc
    ), {}),
    tableValues: tableValues?.map(table => ({ ...table, products: Object.values(table.products) })),
    ...rest
  }))
);

const FillDocument: FC<FillDocumentProps> = ({
  sides, blocksTree, onClickDownload, isDownloadLoading, valueSides
}) => {
  const { currencies, lang } = useContext(ThemeContext);
  const currency = currencies[lang];
  const { t } = useTranslation();
  const [currentSideId, setCurrentSideId] = useState(sides?.[0]?.id ?? '');
  const [sidesData, setSidesData] = useState<ISidesListEditData | undefined>(valueSides);
  // const [ isChanged, setIsChanged ] = useState<boolean>(false);
  const [acceptAgreement, setAcceptAgreement] = useState<boolean>(false);
  const [files, setFiles] = useState<(File | null)[]>([]);
  const [sendingData, setSendingData] = useState<ISidesSendingData | null>(null);
  const [isShowSelect, setIsShowSelect] = useState<boolean>(false);

  const [nameNextSide, setNameNextSide] = useState(
    sides && sides?.length !== 1 && sides[0 + 1].name
  );
  const currentSideIndex = sides?.findIndex(({ id }) => id === currentSideId) ?? -1;

  useEffect(() => {
    if (valueSides)
      setSidesData(valueSides);
    else if (sides)
      setSidesData(
        sides.reduce((acc, { id, tables, tattrFolders, carticles }) => ({
          ...acc,
          [id]: {
            id,
            tattrErrors: {},
            tattrValues: {},
            tableValues: !!tables?.length ? (
              tables.map(({ id, total, showTotalRow }) => ({
                tableId: id,
                products: [],
                total: calculateTotalValues([], mapTotalForCalc({}, total.map(({ id }) => id), showTotalRow), currency.code),
                customProductsIds: []
              }))
            ) : null,
            articleValues: !!carticles?.length ? carticles.map(({ id, variants }) => (
              { id, value: variants.filter(({ default: byDefault }) => byDefault).map(({ id }) => id) }
            )) : null,
            isValid: !tattrFolders.length,
          },
        }), {})
      );

    if (sides?.length) setCurrentSideId(sides[0].id);
  }, [sides, valueSides, currency]);

  const handleChangeSide = (id: string) => {
    setCurrentSideId(id);
    const elem = document.querySelector('.forma-ui__tabs__root');
    if (elem) window.scrollTo({ top: window.scrollY + elem.getBoundingClientRect().top - 70, behavior: 'smooth' });
  };

  const handleClickDownload = () => {
    if (!sidesData) return;
    onClickDownload(
      cleanSideDataForRender(sidesData),
      files
    );
    analytics.clickDownloadDocument();
  };

  const selectedVariants = sidesData && Object.values(sidesData)?.reduce((acc: string[], { articleValues }) => {
    if (!articleValues) return acc;
    return [...acc, ...articleValues.reduce((acc: string[], { value }) => ([...acc, ...value]), [])];
  }, []);

  const sidesToFilling = sides && mapSidesToFilling(sides, blocksTree, selectedVariants);

  useEffect(() => {
    if (!sides) return;
    if (currentSideIndex !== sides.length - 1) {
      setNameNextSide(() => sides[currentSideIndex + 1].name);
    }
  }, [sides, currentSideIndex]);

  const handleButtonTransitionSide = () => {
    if (!isFillingValidOneSide || !currentSideId) return;

    const indexCurrentSide = sides.findIndex((elem) => elem.id === currentSideId);

    if (indexCurrentSide !== sides.length - 1) {
      handleChangeSide(sides[indexCurrentSide + 1].id);
    }
  };

  const sidesFillingCount = sidesToFilling?.map(({ id, tattrFolders, carticles, tables }) => {
    const sideValues = sidesData && sidesData[id];
    const sendingFolders = sendingData?.[id].tattrFolders;
    const sendingArticles = sendingData?.[id].articles;
    const sendingTattrs = sendingFolders && Object.values(sendingFolders).flatMap(items => items);
    const sendingTables = sendingData?.[id].tables;

    let tattrsCount = 0;
    let filledCount = 0;

    tattrFolders.forEach(({ tattrs }) => {
      tattrs.forEach(({ id: tattrId }) => {
        tattrsCount += 1;

        if (sideValues && (
          (!!sideValues.tattrValues[tattrId] && !sideValues.tattrErrors[tattrId])
          || sendingTattrs?.includes(tattrId)
        )) filledCount += 1;
      });
    });

    tables?.forEach(({ id }) => {
      tattrsCount += 1;
      const tableProducts = sideValues && sideValues.tableValues.find(table => id === table.tableId)?.products;
      if (
        (tableProducts && Object.keys(tableProducts).length)
        || (isShowSelect && sendingTables?.includes(id))
      ) filledCount += 1;
    });

    carticles.forEach(({ id }) => {
      tattrsCount += 1;
      if (sideValues && (
        sideValues.articleValues.find(article => id === article.id)?.value?.length
        || sendingArticles?.includes(id)
      )) filledCount += 1;
    });

    return { count: filledCount, total: tattrsCount };
  });

  const currentSideValues = sidesData && sidesData[currentSideId];
  const currentSide = sidesToFilling?.find(side => String(side.id) === String(currentSideId));

  const isFillingValidOneSide =
    sidesData && sidesToFilling && sides &&
    Object.keys(sidesData).length === sides.length &&
    // (!attachments?.length || attachments.length === files.filter(file => !!file).length) &&
    currentSideValues && currentSide &&
    checkSideValid(currentSide, currentSideValues);

  const isFillingValid = !!(sidesData && sidesToFilling && Object.keys(sidesData).length === sides.length)
    && Object.values(sidesData).every(sideData => {
      const side = sidesToFilling.find(side => String(side.id) === String(sideData.id));
      if (!side) return false;
      return checkSideValid(side, sideData);
    });

  const emptyFieldsCount = sidesFillingCount?.reduce((acc, { count, total }) => acc + total - count, 0);

  return (
    <div className={styles.root} id="fill-document">
      {/* <div className={styles.head}>
        <div className={styles.title}>{name}</div>
        {entity && (
          <div className={styles.subtitle}>{t('company_ask_fill_fields', { company: entity.name })}</div>
        )}
      </div> */}
      <Tabs
        showSingleTab={false}
        contentClassName="fill-document-content"
        current={currentSideId}
        onChange={setCurrentSideId}
        items={sidesToFilling?.length ? (
          sidesToFilling.map(({ id, name, tattrFolders, tables, carticles }, index) => {
            const sideValues = sidesData && sidesData[id];
            const articleValues = sideValues?.articleValues;

            // const isSideCarticlesFilled = !carticles?.length
            //   || articleValues?.filter(({ value }) => !!value.length).length === carticles.length;

            return {
              id,
              name,
              testId: `filling_tab_${index}`,
              bage: {
                content: sidesFillingCount && (
                  <span className={classNames(
                    styles.bage,
                    currentSideId === id && styles.active,
                    sidesFillingCount[index].total > sidesFillingCount[index].count ? styles.error : styles.success
                  )}>
                    {sidesFillingCount[index].count}/{sidesFillingCount[index].total}
                  </span>
                )
              },
              children: (
                <FillDocumentSide
                  id={id}
                  name={name}
                  tattrFolders={tattrFolders}
                  tables={tables}
                  carticles={carticles}
                  sideData={sidesData && sidesData[id]}
                  onChangeSidesData={setSidesData}
                  key={id}
                />
              )
            };
          })
        ) : (
          [
            {
              id: '0',
              name: '',
              children: (
                <div className={styles.items}>
                  {[...Array(10)].map((key, index) => (
                    <FillingItemSkelet key={index} />
                  ))}
                </div>
              )
            }
          ]
        )}
      />

      {/* {(sides && entity) && (
        <Checkbox
          containerClass={styles.agreement}
          id="agreement_checkbox"
          name="agreement_checkbox"
          label={<Trans i18nKey="i_agree_privacy_conditions" components={{ link_tag:
          <Link href={`/soglasie_na_obrabotku/${urlKey}`} passHref ><a target='_blank'></a></Link>
        }}/>}
          onChange={e => setAcceptAgreement(e.target.checked)}
          checked={acceptAgreement}
          viewStyle="square"
          size="medium"
        />
      )} */}
      {!!sides && (
        <div className={styles.notice}>
          <div className={styles.noticeButtons}>
            {(sidesToFilling && sidesToFilling.length > 1 && currentSideIndex > 0) && (
              <Button
                className={styles.noticeButton}
                viewStyle="tertiary"
                onClick={() => handleChangeSide(sides[currentSideIndex - 1].id)}
                icon={<ReactSVG src="/icons/arrow-left.svg" wrapper="span" />}
              >
                {sides[currentSideIndex - 1].name}
                {sidesFillingCount && (
                  <span className={classNames(
                    styles.noticeBage,
                    sidesFillingCount[currentSideIndex - 1].total > sidesFillingCount[currentSideIndex - 1].count
                      ? styles.error
                      : styles.success
                  )}>
                    {`${sidesFillingCount[currentSideIndex - 1].count}/${sidesFillingCount[currentSideIndex - 1].total}`}
                  </span>
                )}
              </Button>
            )}
          </div>
          <div className={styles.noticeContent}>
            <div className={classNames(styles.emptyFields, emptyFieldsCount ? styles.error : styles.success)}>
              {isShowSelect
                ? `${t('count_fields', { count: emptyFieldsCount })} ${t('not_selected_not_filled')}`
                : (emptyFieldsCount ? `${t('not_filled')} ${t('count_fields', { count: emptyFieldsCount })}` : 'Все поля заполнены')
              }
            </div>
            {!!emptyFieldsCount && (
              <div className={styles.noticeDescription}>
                Для того чтобы создать документ, заполните пустые поля
              </div>
            )}
          </div>
          <div className={styles.noticeButtons}>
            {(sidesToFilling && sidesToFilling.length > 1 && currentSideIndex < sidesToFilling.length - 1) && (
              <Button
                className={styles.noticeButton}
                viewStyle="tertiary"
                onClick={() => handleChangeSide(sides[currentSideIndex + 1].id)}
                icon={<ReactSVG src="/icons/arrow-right.svg" wrapper="span" />}
                reversed
              >
                {sides[currentSideIndex + 1].name}
                {sidesFillingCount && (
                  <span className={classNames(
                    styles.noticeBage,
                    sidesFillingCount[currentSideIndex + 1].total > sidesFillingCount[currentSideIndex + 1].count
                      ? styles.error
                      : styles.success
                  )}>
                    {`${sidesFillingCount[currentSideIndex + 1].count}/${sidesFillingCount[currentSideIndex + 1].total}`}
                  </span>
                )}
              </Button>
            )}
          </div>
        </div>
      )}
      {sides && (
        <div>
          <ButtonsContainer className={styles.buttons}>
            {sides.length !== 1 && sides.length - 1 !== sides.findIndex((elem) => elem.id === currentSideId) && (
              <Button
                viewStyle="tertiary"
                className={styles.button}
                disabled={!isFillingValidOneSide}
                onClick={handleButtonTransitionSide}
                fullWidth
              >
                {t('fill_side', { name: nameNextSide })}
              </Button>
            )}
            <LoadingButton
              id="save_filled_button"
              type="submit"
              className={styles.button}
              viewStyle="primary"
              onClick={handleClickDownload}
              disabled={!isFillingValid && !acceptAgreement}
              isLoading={isDownloadLoading}
              fullWidth
              data-testid="save_filled_button"
            >
              {t('download_document')}
            </LoadingButton>
          </ButtonsContainer>
          <div className={styles.buttonsNotice}>
            <Trans
              i18nKey="privacy_form_notice"
              components={{ privacy: <Link href="/privacy" /> }}
              values={{ button: t('download_document') }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FillDocument;
